/**
 * Cliente API generico para comunicarse con los webhooks de n8n
 * Base URL: https://agent.johnfox.click/webhook
 *
 * Este modulo contiene SOLO la infraestructura core (apiRequest, helpers,
 * tipos genericos). Los wrappers de dominio viven en features/{modulo}/api.ts.
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'https://agent.johnfox.click/webhook';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export interface ApiOptions {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  token?: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ─── TOKEN GETTER (lectura en runtime desde el store de auth) ─────────
// Import estatico: el ciclo api.ts ↔ auth-store.ts es seguro porque
// getToken() solo se ejecuta en runtime, no durante la inicializacion del modulo.
import { useAuthStore } from '@/lib/auth-store';
import { logger, generarCorrelationId } from '@/lib/logger';

// Devuelve la categoria del logger en base al path del endpoint.
// Permite diferenciar 'auth' / 'onboarding' del resto ('api') sin tocar
// los callers — la unica fuente de verdad es la URL del request.
function categoriaDesdeEndpoint(endpoint: string): 'auth' | 'onboarding' | 'api' {
  if (endpoint.startsWith('/api/auth') || endpoint.startsWith('/auth/')) return 'auth';
  if (endpoint.startsWith('/onboarding/')) return 'onboarding';
  return 'api';
}

export function getToken(): string | null {
  try {
    return useAuthStore.getState().token;
  } catch {
    return null;
  }
}

// ─── ACTIVITY LOG: SANITIZACION Y EMISION ─────────────────────────────

const SENSITIVE_KEYS = [
  'password',
  'token',
  'secret',
  'api_key',
  'access_token',
  'refresh_token',
  'otp',
  'pin',
];
const MUTATING_METHODS: HttpMethod[] = ['POST', 'PUT', 'DELETE', 'PATCH'];

// Convención del backend n8n: el portal usa POST con `_method` en el body
// para diferenciar GET/PUT/DELETE/TEST. Cuando `_method` indica una
// operación read-only, el HTTP method (POST) NO refleja una mutación
// real — y el Activity Log no debe emitirse.
const READ_ONLY_BODY_METHODS = new Set(['GET', 'TEST']);

function readBodyMethod(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const m = (body as Record<string, unknown>)._method;
  return typeof m === 'string' ? m.toUpperCase() : null;
}

function sanitizePayload(obj: unknown): unknown {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sanitizePayload);
  const clean: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    if (SENSITIVE_KEYS.some((sk) => k.toLowerCase().includes(sk))) {
      clean[k] = '[REDACTED]';
    } else {
      clean[k] = v;
    }
  }
  return clean;
}

function extractRouteInfo(endpoint: string): {
  modulo: string;
  entidad: string;
  entidadId: string | null;
} {
  const path = endpoint.split('?')[0];
  const apiMatch = path.match(/\/api\/(.+)/);
  if (!apiMatch) {
    return { modulo: 'desconocido', entidad: 'desconocido', entidadId: null };
  }

  const parts = apiMatch[1].split('/').filter(Boolean);
  const modulo = parts[0] || 'desconocido';
  const entidad = parts[1] || parts[0] || 'desconocido';

  let entidadId: string | null = null;
  for (const part of parts) {
    if (
      part.match(/^[0-9a-f-]{8,}$/i) ||
      part.match(/^\d+$/) ||
      part.match(/^(cont|ord|res|fac|srv)_/)
    ) {
      entidadId = part;
      break;
    }
  }

  return { modulo, entidad, entidadId };
}

function methodToAccion(method: HttpMethod): string {
  switch (method) {
    case 'POST':
      return 'CREATE';
    case 'PUT':
    case 'PATCH':
      return 'UPDATE';
    case 'DELETE':
      return 'DELETE';
    default:
      return 'UNKNOWN';
  }
}

interface ActivityLogEntry {
  accion: string;
  metodo_http: string;
  endpoint: string;
  modulo: string;
  entidad: string;
  entidad_id: string | null;
  payload_enviado: unknown;
  respuesta: unknown;
  pagina_origen: string;
  before_state?: unknown;
  timestamp: string;
}

function emitActivityLog(entry: ActivityLogEntry, token: string): void {
  // Fire-and-forget: no bloqueamos la respuesta del request principal.
  // Evitamos loop infinito si el endpoint mismo es el de actividad.
  if (entry.endpoint.includes('/api/actividad')) return;

  const url = `${API_URL}/api/actividad`;
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(entry),
  }).catch((err) => {
    console.warn('[ActivityLog] Failed to emit log entry:', err?.message);
  });
}

// ─── REFRESH DE TOKEN (singleton para evitar carreras) ────────────────

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

async function tryRefreshToken(): Promise<boolean> {
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const authStorage = localStorage.getItem('johnfox-auth');
      if (!authStorage) return false;

      const { state } = JSON.parse(authStorage);
      const refreshToken = state?.refreshToken;

      if (!refreshToken) return false;

      const response = await fetch(`${API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();

      if (data.success && data.data?.token) {
        const newState = {
          ...state,
          token: data.data.token,
          refreshToken: data.data.refreshToken,
        };
        localStorage.setItem(
          'johnfox-auth',
          JSON.stringify({ state: newState, version: 0 })
        );
        return true;
      }

      return false;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ─── CORE apiRequest FUSIONADO ────────────────────────────────────────

export async function apiRequest<T>(
  endpoint: string,
  options: ApiOptions = {},
  logOptions?: { before?: unknown }
): Promise<ApiResponse<T>> {
  const { method = 'GET', body, headers = {}, token } = options;

  // 1) Construir headers + config (cache no-store siempre)
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers: requestHeaders,
    cache: 'no-store',
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  const url = `${API_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  // Token efectivo para Activity Log (puede mutar si hay refresh exitoso)
  let effectiveToken: string | null = token ?? null;

  // Observabilidad: correlation id para empatar start/end en el buffer del logger.
  // Sin cuerpos ni tokens — solo url + method + status + duration.
  const correlationId = generarCorrelationId();
  const categoria = categoriaDesdeEndpoint(endpoint);
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();

  logger.log({
    category: categoria,
    event: 'request_start',
    meta: { url: endpoint, method, correlation_id: correlationId },
  });

  try {
    // 2) Fetch -> si 401 con token -> refresh -> re-fetch
    let response = await fetch(url, config);

    if (response.status === 401 && token) {
      const refreshed = await tryRefreshToken();

      if (refreshed) {
        const authStorage =
          typeof window !== 'undefined'
            ? localStorage.getItem('johnfox-auth')
            : null;
        if (authStorage) {
          const { state } = JSON.parse(authStorage);
          const newToken = state?.token;

          if (newToken) {
            requestHeaders['Authorization'] = `Bearer ${newToken}`;
            config.headers = requestHeaders;
            effectiveToken = newToken;
            response = await fetch(url, config);
          }
        }
      }
    }

    // 3) Parsear JSON
    const data = await response.json();

    // Logger: request_end con status + duration (sin body ni token).
    const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    logger.log({
      category: categoria,
      event: 'request_end',
      meta: {
        url: endpoint,
        method,
        status: response.status,
        duration_ms: Math.round(t1 - t0),
        correlation_id: correlationId,
      },
    });

    // 4) Si !ok -> lanzar ApiError
    if (!response.ok) {
      throw new ApiError(
        data.error || data.message || 'Error en la solicitud',
        response.status
      );
    }

    // 5) Activity Log fire-and-forget en mutaciones exitosas.
    //    Skip si el body lleva `_method: 'GET'` o `_method: 'TEST'` —
    //    son lecturas read-only disfrazadas como POST por la convención
    //    del backend n8n, y NO deben generar entries en activity_log.
    const bodyMethod = readBodyMethod(body);
    const isReadOnlyBody =
      bodyMethod !== null && READ_ONLY_BODY_METHODS.has(bodyMethod);

    if (
      effectiveToken &&
      MUTATING_METHODS.includes(method) &&
      !isReadOnlyBody
    ) {
      const { modulo, entidad, entidadId } = extractRouteInfo(endpoint);
      emitActivityLog(
        {
          accion: methodToAccion(method),
          metodo_http: method,
          endpoint,
          modulo,
          entidad,
          entidad_id: entidadId,
          payload_enviado: sanitizePayload(body),
          respuesta: sanitizePayload(data),
          pagina_origen:
            typeof window !== 'undefined' ? window.location.pathname : '',
          before_state: logOptions?.before
            ? sanitizePayload(logOptions.before)
            : null,
          timestamp: new Date().toISOString(),
        },
        effectiveToken
      );
    }

    // 6) Retornar data
    return data;
  } catch (error) {
    // Logger: request_failed con error_message (sin stacktrace ni body).
    // ApiError ya esta logueado por el request_end previo (status != 200),
    // pero un fallo de red o un throw post-parse cae aca sin haber emitido end.
    const errorMessage = error instanceof Error ? error.message : 'error desconocido';
    logger.log({
      category: categoria,
      event: 'request_failed',
      meta: {
        url: endpoint,
        method,
        error_message: errorMessage,
        correlation_id: correlationId,
      },
    });
    // Flush fire-and-forget: queremos el buffer en el server cuando algo se rompe.
    logger.flush('error').catch(() => {
      // si el flush falla no rompemos el error original
    });

    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError('Error de conexion con el servidor', 500);
  }
}

// ─── HELPERS CON before_state ─────────────────────────────────────────

export function putWithLog<T>(
  endpoint: string,
  body: unknown,
  token: string,
  before: unknown
): Promise<ApiResponse<T>> {
  return apiRequest<T>(endpoint, { method: 'PUT', body, token }, { before });
}

export function deleteWithLog<T>(
  endpoint: string,
  token: string,
  before: unknown
): Promise<ApiResponse<T>> {
  return apiRequest<T>(endpoint, { method: 'DELETE', token }, { before });
}

// ─── UTILS ────────────────────────────────────────────────────────────

export function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

// ─── TIPO UTILITARIO GENERICO ─────────────────────────────────────────

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
}

